import React from 'react';
import { useTranslation } from 'react-i18next';

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

export default function HoursInput({ value = {}, onChange }) {
  const safe = v => v || {};
  const { t } = useTranslation();
  
  const handleDay = (day, patch) => {
    const currentDay = safe(value[day]);
    let newPatch = { ...patch };

    // Smart default: 09:00 - 18:00
    if (patch.isOpen && !currentDay.opening && !currentDay.closing) {
      newPatch.opening = "09:00";
      newPatch.closing = "18:00";
    }

    const next = { ...safe(value), [day]: { ...currentDay, ...newPatch } };
    onChange(next);
  };

  const handleTop = (patch) => onChange({ ...safe(value), ...patch });

  const applyMondayToAll = () => {
    const monday = safe(value['Monday']);
    if (!monday.isOpen) return alert(t('general.time.hours.alert-monday'));
    const next = { ...safe(value) };
    DAYS.forEach(day => { next[day] = { ...monday }; });
    onChange(next);
  };

  // Reset
  const resetHours = () => {
    if (window.confirm(t('general.time.hours.alert-confirm-reset'))) {
      onChange({});
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <label className="text-gray-700 text-sm font-bold"> {t('general.time.hours.business-hours')} </label>
        {Object.keys(value).length > 0 && (
          <button 
            type="button" 
            onClick={resetHours}
            className="text-[10px] text-red-500 font-bold hover:underline"
          >
             {t('general.reset')}
          </button>
        )}
      </div>

      {/* Global Checkboxes */}
      <div className="grid grid-cols-2 gap-2 bg-gray-50 p-2 rounded-lg border border-gray-100">
        <label className="flex items-center text-xs cursor-pointer p-1">
          <input
            type="checkbox"
            checked={!!value.isOpen247}
            onChange={e => handleTop({ isOpen247: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 mr-2"
          />
           {t('general.time.hours.24-7')}
        </label>
        <label className="flex items-center text-xs cursor-pointer p-1">
          <input
            type="checkbox"
            checked={!!value.variableHours}
            onChange={e => handleTop({ variableHours: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 mr-2"
          />
           {t('general.time.hours.variable-hours')}
        </label>
      </div>

      {/* Days List */}
      <div className="space-y-3">
        {DAYS.map((day) => {
          const dayVal = safe(value[day]);
          const isClosed = !dayVal.isOpen || !!value.isOpen247;
          
          return (
            <div key={day} className="flex flex-col sm:flex-row sm:items-center gap-2 pb-2 border-b border-gray-50 last:border-0">
              <div className="flex items-center justify-between sm:justify-start sm:gap-4">
                <span className="text-sm font-semibold text-gray-700 w-16"> {t(`general.time.days.${day.toLowerCase()}`)} </span>
                <div className="flex items-center gap-2">
                   <span className="text-[10px] text-gray-400 uppercase">{dayVal.isOpen ? t('general.time.hours.open') : t('general.time.hours.closed')}</span>
                   <input
                    type="checkbox"
                    checked={!!dayVal.isOpen}
                    onChange={e => handleDay(day, { isOpen: e.target.checked })}
                    className="w-6 h-6 rounded-full border-gray-300 text-green-600 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className={`flex items-center gap-2 transition-opacity ${isClosed ? 'opacity-30 pointer-events-none' : 'opacity-100'}`}>
                <input
                  type="time"
                  value={dayVal.opening || ''}
                  onChange={e => handleDay(day, { opening: e.target.value })}
                  className="px-2 py-2 border rounded-lg text-sm bg-white shadow-sm w-full sm:w-32"
                />
                <span className="text-gray-400 text-xs font-bold"> {t('general.time.hours.to')} </span>
                <input
                  type="time"
                  value={dayVal.closing || ''}
                  onChange={e => handleDay(day, { closing: e.target.value })}
                  className="px-2 py-2 border rounded-lg text-sm bg-white shadow-sm w-full sm:w-32"
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      {safe(value['Monday']).isOpen && (
        <button
          type="button"
          onClick={applyMondayToAll}
          className="w-full py-3 bg-blue-600 text-white text-xs font-bold rounded-xl shadow-md active:scale-95 transition-transform"
        >
           {t('general.time.hours.copy-monday')}
        </button>
      )}
    </div>
  );
}
